import { useCallback, useEffect, useState } from "react";
import { coreToast } from "../../core";
import i18nPackage from "../../i18n/i18n";
import { useEditQuizToast } from "../context/EditQuizContext";
import { ViewDetailsModal } from "../edit-quiz-modals";
import { ProfileListProps } from "../profile/ProfileList";
import {
	ExemptionInfo,
	removeStudentsFromExemptRoster,
	updateStudentsInRoster,
	useAccommodationFilter,
	useExemptionModals,
	useStudentSelection,
} from "./exemptionHelper";
import AccommodationTable, { AccommodationTableProps } from "./AccommodationTable";
import SelectAccommodationPage from "./SelectAccommodationPage";
import { ExemptedStudent } from "../..";

export type ExemptionSectionView = "table" | "details" | "select";

export type ExemptionSectionProps = {
	exemptionInfo: ExemptionInfo;
	view?: ExemptionSectionView;
	onViewChange?: (view: ExemptionSectionView) => void;
	// Will return the new exemption info
	onRegisterSave?: (saveFn: (() => ExemptionInfo) | null) => void;
	onRosterChange: (newRoster: ExemptionInfo) => void;
	/**If passed, will render a save button in the Select Accommodation Page.*/
	hasInternalSave?: boolean;
	/**If passed, will be called when the draft is invalid. */
	onInvalidDraft?: (invalid: boolean) => void;
} & Pick<
	ProfileListProps,
	| "verityInstituteSettings"
	| "enforcedProfilesOnly"
	| "optionRelatedInstituteSettings"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
	| "lms"
> &
	Pick<
		AccommodationTableProps,
		"loading" | "profilesMap" | "editInstitutionExemptions" | "backButtonText" | "onBack" | "presentation"
	>;

export default function ExemptionSection({
	loading,
	profilesMap,
	exemptionInfo,
	editInstitutionExemptions,
	enforcedProfilesOnly,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	proctoringData,
	canDeleteProfile,
	onDelete,
	onViewChange,
	onRegisterSave,
	onRosterChange,
	onInvalidDraft,
	hasInternalSave,
	view,
	backButtonText,
	onBack,
	presentation,
	lms,
}: ExemptionSectionProps) {
	const [curPage, setCurPage] = useState<number>(1);
	const [recentIds, setRecentIds] = useState<Set<number>>(new Set());
	const toastContainerId = useEditQuizToast();

	const rosterControls = useAccommodationFilter({
		exemptionInfo,
		recentIds,
		profilesMap,
	});
	const { filteredRoster, clearFilters } = rosterControls;

	const studentSelectControls = useStudentSelection(filteredRoster, exemptionInfo);
	const { selectedStudents, clearSelection } = studentSelectControls;

	const modalControls = useExemptionModals(profilesMap, selectedStudents, view ?? "table", onViewChange);
	const { viewModal, accommodationModal, currentStudent, curStudentProfile, accommodationView } = modalControls;

	const { draft, updateDraft } = accommodationModal;

	const clearRecentIds = useCallback(() => {
		if (recentIds.size > 0) {
			setRecentIds(new Set());
		}
	}, [recentIds.size]);

	const handleUpdateAccommodation = useCallback(() => {
		if (!selectedStudents || !draft) return exemptionInfo;

		const updatedIds = selectedStudents.map((s) => s.userId);
		setRecentIds(new Set(updatedIds));

		const newRoster = updateStudentsInRoster(
			exemptionInfo,
			selectedStudents,
			draft.selectedProfileId,
			draft.selectedNotes,
		);

		coreToast.dismiss();
		if (!editInstitutionExemptions) {
			const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;
			if (selectedStudents?.length === 1) {
				const studentName = selectedStudents[0].name || i18nPackage.t("editQuizPage.accommodationStep.defaultName");
				coreToast.success(
					i18nPackage.t("editQuizPage.accommodationStep.accommodationsSaved_one", {
						name: studentName,
					}),
					toastOptions,
				);
			} else {
				coreToast.success(i18nPackage.t("editQuizPage.accommodationStep.accommodationsSaved_other"), toastOptions);
			}
		}
		setCurPage(1);
		clearSelection();
		clearFilters();
		onRosterChange(newRoster);

		accommodationModal.close();

		return newRoster;
	}, [
		selectedStudents,
		draft,
		exemptionInfo,
		editInstitutionExemptions,
		clearSelection,
		clearFilters,
		onRosterChange,
		accommodationModal,
		toastContainerId,
	]);

	const handleRemoveAccommodations = useCallback(
		(student?: ExemptedStudent) => {
			const updatedRoster = removeStudentsFromExemptRoster(exemptionInfo, student ? [student] : selectedStudents);

			onRosterChange(updatedRoster);
			clearSelection();
		},
		[clearSelection, exemptionInfo, onRosterChange, selectedStudents],
	);

	useEffect(() => {
		if (!onRegisterSave) return;
		if (accommodationView === "select") {
			onRegisterSave(handleUpdateAccommodation);
		} else {
			onRegisterSave(null);
		}
		return () => {
			onRegisterSave(null);
		};
	}, [accommodationView, handleUpdateAccommodation, onRegisterSave]);

	return (
		<>
			{accommodationView === "table" && (
				<AccommodationTable
					clearRecentIds={clearRecentIds}
					curPage={curPage}
					modalControls={modalControls}
					profilesMap={profilesMap}
					rosterControls={rosterControls}
					studentSelectControls={studentSelectControls}
					onCurPageChange={setCurPage}
					editInstitutionExemptions={editInstitutionExemptions}
					loading={loading}
					onBack={onBack}
					backButtonText={backButtonText}
					onRemoveAccommodation={handleRemoveAccommodations}
					presentation={presentation}
				/>
			)}
			{accommodationView === "select" && (
				<SelectAccommodationPage
					profiles={Array.from(profilesMap.values())}
					selectedProfileId={draft.selectedProfileId}
					studentAccommodationNotes={draft.selectedNotes}
					proctoringData={proctoringData}
					onChange={updateDraft}
					onBack={accommodationModal.close}
					enforcedProfilesOnly={enforcedProfilesOnly}
					optionRelatedInstituteSettings={optionRelatedInstituteSettings}
					verityInstituteSettings={verityInstituteSettings}
					canDeleteProfile={canDeleteProfile}
					onDelete={onDelete}
					onSave={hasInternalSave ? handleUpdateAccommodation : undefined}
					onInvalidDraft={onInvalidDraft}
					lms={lms}
				/>
			)}
			{accommodationView === "details" && (
				<ViewDetailsModal
					exemptedStudent={currentStudent}
					profile={curStudentProfile}
					onClose={viewModal.close}
					{...(curStudentProfile
						? {
								configuration: {
									optionRelatedInstituteSettings,
									proctoringData,
									verityInstituteSettings,
									proctoringOption: curStudentProfile.proctoringOption,
								},
							}
						: {})}
					description={i18nPackage.t("editQuizPage.accommodationStep.viewDetailsDesc")}
					backButtonText={i18nPackage.t("editQuizPage.accommodationModal.backButton")}
				/>
			)}
		</>
	);
}
